import { defineStore } from 'pinia'
import { UserDataRepository } from '@/repositories/userDataRepository.js'
import { toUserMessage } from '@/services/errorService.js'
import {
  applyQuestionAnswer,
  type QuestionAnswerSubmission,
} from '@/services/practice/answerRecord.js'
import {
  finishPracticeSession as finishSession,
  startPracticeSession as createSession,
  type PracticeSessionInput,
} from '@/services/practice/practiceSession.js'
import { scheduleNextReview, dateKey } from '@/services/review/reviewAlgorithm.js'
import type { Question } from '@/types/knowledge.js'
import type {
  LearningProgress,
  MasteryLevel,
  PracticeSession,
  UserDatabase,
} from '@/types/user-data.js'

const repository = new UserDataRepository()

interface AppState {
  initialized: boolean
  loading: boolean
  error: string | null
  database: UserDatabase | null
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    initialized: false,
    loading: false,
    error: null,
    database: null,
  }),
  actions: {
    async initialize(): Promise<void> {
      if (this.loading || this.initialized) return
      this.loading = true
      this.error = null
      try {
        this.database = await repository.load()
        this.initialized = true
      } catch (error) {
        this.error = toUserMessage(error)
      } finally {
        this.loading = false
      }
    },
    reportError(error: unknown): void {
      this.error = toUserMessage(error)
    },
    clearError(): void {
      this.error = null
    },
    async updateKnowledgeProgress(
      knowledgeId: string,
      patch: Partial<Omit<LearningProgress, 'knowledgeId'>>,
    ): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')

      const current = this.database.progress[knowledgeId] ?? {
        knowledgeId,
        masteryLevel: 0,
        studyCount: 0,
        favorite: false,
        wrongCount: 0,
      }
      const nextDatabase: UserDatabase = {
        ...this.database,
        progress: {
          ...this.database.progress,
          [knowledgeId]: { ...current, ...patch, knowledgeId },
        },
      }

      try {
        this.database = await repository.save(nextDatabase)
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async recordStudyResult(
      knowledgeId: string,
      masteryLevel: Exclude<MasteryLevel, 0>,
      kind: 'new' | 'review',
      studiedAt = new Date(),
    ): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')

      const current = this.database.progress[knowledgeId] ?? {
        knowledgeId,
        masteryLevel: 0 as const,
        studyCount: 0,
        favorite: false,
        wrongCount: 0,
      }
      const timestamp = studiedAt.toISOString()
      const statisticDate = dateKey(studiedAt)
      const existingStatistic = this.database.dailyStatistics.find(
        (statistic) => statistic.date === statisticDate,
      )
      const nextStatistics = existingStatistic
        ? this.database.dailyStatistics.map((statistic) =>
            statistic.date === statisticDate
              ? {
                  ...statistic,
                  learned: statistic.learned + (kind === 'new' ? 1 : 0),
                  reviewed: statistic.reviewed + (kind === 'review' ? 1 : 0),
                }
              : statistic,
          )
        : [
            ...this.database.dailyStatistics,
            {
              date: statisticDate,
              learned: kind === 'new' ? 1 : 0,
              reviewed: kind === 'review' ? 1 : 0,
              answered: 0,
              correct: 0,
            },
          ]

      const nextDatabase: UserDatabase = {
        ...this.database,
        progress: {
          ...this.database.progress,
          [knowledgeId]: {
            ...current,
            knowledgeId,
            masteryLevel,
            studyCount: current.studyCount + 1,
            lastStudyAt: timestamp,
            nextReviewAt: scheduleNextReview(masteryLevel, studiedAt),
          },
        },
        recentKnowledgeId: knowledgeId,
        dailyStatistics: nextStatistics,
      }

      try {
        this.database = await repository.save(nextDatabase)
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async updateSettings(patch: Partial<UserDatabase['settings']>): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      try {
        this.database = await repository.save({
          ...this.database,
          settings: { ...this.database.settings, ...patch },
        })
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async exportUserData(): Promise<string> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      try {
        return await repository.export()
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async importUserData(serialized: string): Promise<void> {
      try {
        const database = await repository.import(serialized)
        this.database = database
        this.initialized = true
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async clearUserData(): Promise<void> {
      try {
        await repository.clear()
        this.database = await repository.load()
        this.initialized = true
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async recordQuestionAnswer(
      question: Question,
      selectedAnswer: string[],
      submission: QuestionAnswerSubmission,
    ): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      const nextDatabase = applyQuestionAnswer(this.database, question, selectedAnswer, submission)

      try {
        this.database = await repository.save(nextDatabase)
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async startPracticeSession(
      input: PracticeSessionInput & { id: string },
    ): Promise<PracticeSession> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      const nextDatabase = createSession(this.database, input)

      try {
        this.database = await repository.save(nextDatabase)
        const session = this.database.practiceSessions[input.id]
        if (!session) throw new Error(`练习 Session ${input.id} 保存失败。`)
        return session
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async finishPracticeSession(sessionId: string, finishedAt = new Date()): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      const nextDatabase = finishSession(this.database, sessionId, finishedAt)
      if (nextDatabase === this.database) return

      try {
        this.database = await repository.save(nextDatabase)
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async removeWrongQuestion(questionId: string): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      if (!this.database.wrongQuestions[questionId]) return

      const nextWrongQuestions = { ...this.database.wrongQuestions }
      delete nextWrongQuestions[questionId]
      try {
        this.database = await repository.save({
          ...this.database,
          wrongQuestions: nextWrongQuestions,
        })
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
    async setWrongQuestionMastered(questionId: string, mastered: boolean): Promise<void> {
      if (!this.database) await this.initialize()
      if (!this.database) throw new Error('用户数据尚未初始化。')
      const record = this.database.wrongQuestions[questionId]
      if (!record || record.mastered === mastered) return

      try {
        this.database = await repository.save({
          ...this.database,
          wrongQuestions: {
            ...this.database.wrongQuestions,
            [questionId]: { ...record, mastered },
          },
        })
      } catch (error) {
        this.reportError(error)
        throw error
      }
    },
  },
})
